import {
    CreateGroupSiteResponse,
    CreateGroupSiteServerResponse,
} from './schema/CreateGroupSiteResponse';
import { ensureAuthToken } from './authentication';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import { HttpReadyState, HttpStatusCode } from 'owa-http-status-codes';

const ACCEPT_HEADER = 'application/json;odata=verbose';
const ACCEPT_TAG = 'Accept';
const AUTHORIZATION_TAG = 'Authorization';
const BEARER_TOKEN_HEADER_PREFIX = 'Bearer ';
const CREATE_GROUP_SP_SITE_URL_PREFIX = '/_api/GroupSiteManager/Create?groupId=';

export function createGroupSite(
    groupSiteRootUrl: string,
    groupId: string,
    actionSource: 'GroupFilesHubPollGroupSite'
): Promise<CreateGroupSiteResponse> {
    const datapoint = new PerformanceDatapoint(actionSource + 'createGroupSite');
    if (!groupSiteRootUrl) {
        const error = new Error('GroupSiteUrl is null or empty.');
        datapoint.endWithError(DatapointStatus.ClientError, error);
        return Promise.reject(error);
    }

    if (!groupId) {
        const error = new Error('GroupId is null or empty.');
        datapoint.endWithError(DatapointStatus.ClientError, error);
        return Promise.reject(error);
    }

    return new Promise<CreateGroupSiteResponse>(async (resolve, reject) => {
        let accessToken;
        try {
            datapoint.addCheckmark('GetSPAccessTokenStart');
            accessToken = await ensureAuthToken(groupSiteRootUrl, actionSource);
            datapoint.addCheckmark('GetSPAccessTokenDone');
            if (!accessToken) {
                throw new Error('AccessToken is null or empty.');
            }
        } catch (error) {
            datapoint.endWithError(
                DatapointStatus.ServerError,
                error instanceof Error ? error : new Error(JSON.stringify(error))
            );
            reject(new Error('Failed to get SP access token.'));
            return;
        }

        const url = buildRequestUrl(groupSiteRootUrl, groupId);
        const request = new XMLHttpRequest();
        request.open('POST', url, true);
        request.setRequestHeader(AUTHORIZATION_TAG, BEARER_TOKEN_HEADER_PREFIX + accessToken);
        request.setRequestHeader(ACCEPT_TAG, ACCEPT_HEADER);
        request.onreadystatechange = function() {
            if (request.readyState === HttpReadyState.HeadersReceived) {
                datapoint.addCustomProperty(
                    'sprequestguid',
                    request.getResponseHeader('sprequestguid')
                );
            }

            if (request.readyState === HttpReadyState.Done) {
                if (request.status === HttpStatusCode.OK) {
                    let response: CreateGroupSiteServerResponse;
                    try {
                        datapoint.addCheckmark('ParseResponseStart');
                        response = JSON.parse(request.response) as CreateGroupSiteServerResponse;
                        datapoint.addCheckmark('ParseResponseDone');
                        if (!response || !response.d || !response.d.Create) {
                            throw new Error('Invalid response.');
                        }
                    } catch (error) {
                        datapoint.endWithError(
                            DatapointStatus.ClientError,
                            error instanceof Error ? error : new Error(JSON.stringify(error))
                        );
                        reject(new Error('Failed to parse response.'));
                        return;
                    }

                    datapoint.end();
                    resolve(response.d.Create);
                } else {
                    datapoint.endWithError(
                        DatapointStatus.ServerError,
                        new Error(request.responseText)
                    );
                    reject(
                        new Error(
                            'Request failed with status ' +
                                request.status +
                                '. Message: ' +
                                request.responseText
                        )
                    );
                }
            }
        };

        request.send();
    });
}

function buildRequestUrl(groupSiteRootUrl: string, groupId: string): string {
    // Full request would look like : "https://microsoft.sharepoint.com/_api/GroupSiteManager/Create?groupId='ab4a6f4e-5293-4417-945b-15748c01874d'"
    return groupSiteRootUrl + CREATE_GROUP_SP_SITE_URL_PREFIX + `'${groupId}'`;
}
